import { apiClient } from '@/core/api-client/client';

export interface FeatureFlag {
  id: string;
  key: string;
  description: string | null;
  enabled: boolean;
  updated_at?: string | null;
}

export interface FeatureFlagUpdate {
  enabled: boolean;
  description?: string | null;
}

export interface Agency {
  id: string;
  name: string;
  code: string;
  country: string | null;
  contact_email: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
  status: string;
  created_at?: string | null;
}

export interface AgencyDetail extends Agency {
  agents: AgencyAgent[];
  commissions: CommissionRule[];
  agent_count: number;
  commission_count: number;
}

export interface AgencyCreate {
  name: string;
  code: string;
  country?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
}

export interface AgencyUpdate {
  name?: string;
  country?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  website?: string | null;
  notes?: string | null;
  status?: string;
}

export interface AgencyAgent {
  id: string;
  agency_id: string;
  user_id: string;
  is_lead: boolean;
  created_at?: string | null;
  email?: string | null;
  full_name?: string | null;
  user_status?: string | null;
}

export interface AgencyAgentCreate {
  email: string;
  password: string;
  full_name: string;
  is_lead?: boolean;
}

export interface CommissionRule {
  id: string;
  name: string;
  university_id: string | null;
  agency_id: string | null;
  school_id: string | null;
  rate_percent: number | string;
  flat_amount: number | string | null;
  currency: string;
  is_active: boolean;
  created_at?: string | null;
}

export interface CommissionRuleCreate {
  name: string;
  university_id?: string | null;
  agency_id?: string | null;
  school_id?: string | null;
  rate_percent?: number;
  flat_amount?: number | null;
  currency?: string;
  is_active?: boolean;
}

export type CommissionRuleUpdate = Partial<CommissionRuleCreate>;

export interface BillingCheckoutRequest {
  school_id: string;
  plan?: string;
  success_url: string;
  cancel_url: string;
}

export interface BillingCheckoutResponse {
  checkout_url: string | null;
  mode: string;
  detail: string;
}

export async function listFeatureFlags(): Promise<FeatureFlag[]> {
  const { data } = await apiClient.get<FeatureFlag[]>('/royal-overseas/feature-flags');
  return data;
}

export async function updateFeatureFlag(
  id: string,
  body: FeatureFlagUpdate,
): Promise<FeatureFlag> {
  const { data } = await apiClient.patch<FeatureFlag>(
    `/royal-overseas/feature-flags/${id}`,
    body,
  );
  return data;
}

export async function listAgencies(): Promise<Agency[]> {
  const { data } = await apiClient.get<Agency[]>('/royal-overseas/agencies');
  return data;
}

export async function getAgency(id: string): Promise<AgencyDetail> {
  const { data } = await apiClient.get<AgencyDetail>(`/royal-overseas/agencies/${id}`);
  return data;
}

export async function createAgency(body: AgencyCreate): Promise<Agency> {
  const { data } = await apiClient.post<Agency>('/royal-overseas/agencies', body);
  return data;
}

export async function updateAgency(id: string, body: AgencyUpdate): Promise<Agency> {
  const { data } = await apiClient.patch<Agency>(`/royal-overseas/agencies/${id}`, body);
  return data;
}

export async function listCommissions(params?: {
  agency_id?: string;
  university_id?: string;
  school_id?: string;
}): Promise<CommissionRule[]> {
  const { data } = await apiClient.get<CommissionRule[]>('/royal-overseas/commissions', {
    params: params ?? undefined,
  });
  return data;
}

export async function createCommission(body: CommissionRuleCreate): Promise<CommissionRule> {
  const { data } = await apiClient.post<CommissionRule>('/royal-overseas/commissions', body);
  return data;
}

export async function updateCommission(
  id: string,
  body: CommissionRuleUpdate,
): Promise<CommissionRule> {
  const { data } = await apiClient.patch<CommissionRule>(
    `/royal-overseas/commissions/${id}`,
    body,
  );
  return data;
}

export async function deleteCommission(id: string): Promise<void> {
  await apiClient.delete(`/royal-overseas/commissions/${id}`);
}

export async function createBillingCheckout(
  body: BillingCheckoutRequest,
): Promise<BillingCheckoutResponse> {
  const { data } = await apiClient.post<BillingCheckoutResponse>(
    '/royal-overseas/billing/checkout',
    body,
  );
  return data;
}

export interface PlatformPlan {
  id: string;
  key: string;
  name: string;
  description: string | null;
  tenant_type: string;
  monthly_price: number | string;
  currency: string;
  modules: string[];
  limits?: PlanLimits | null;
  is_active: boolean;
  sort_order: number;
  updated_at?: string | null;
}

export interface PlanLimits {
  max_students?: number | null;
  max_teachers?: number | null;
  max_counselors?: number | null;
  max_parents?: number | null;
  max_classes?: number | null;
  channels?: { email?: boolean; sms?: boolean; push?: boolean };
  monthly_email?: number | null;
  monthly_sms?: number | null;
  monthly_push?: number | null;
  max_storage_mb?: number | null;
}

export interface PlanUsage {
  plan: string;
  limits: PlanLimits;
  usage: {
    students: number;
    teachers: number;
    counselors: number;
    parents: number;
    classes: number;
    email_month: number;
    sms_month: number;
    push_month: number;
    storage_mb: number;
  };
  near_limit: string[];
}

export interface PlatformPlanUpdate {
  name?: string;
  description?: string | null;
  monthly_price?: number;
  currency?: string;
  modules?: string[];
  limits?: PlanLimits | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface PlatformPlanCreate {
  key: string;
  name: string;
  description?: string | null;
  tenant_type?: string;
  monthly_price?: number;
  currency?: string;
  modules?: string[];
  limits?: PlanLimits | null;
  is_active?: boolean;
  sort_order?: number;
}

export async function listPlans(tenantType = 'school'): Promise<PlatformPlan[]> {
  const { data } = await apiClient.get<PlatformPlan[]>('/royal-overseas/plans', {
    params: { tenant_type: tenantType },
  });
  return data;
}

export async function createPlan(body: PlatformPlanCreate): Promise<PlatformPlan> {
  const { data } = await apiClient.post<PlatformPlan>('/royal-overseas/plans', body);
  return data;
}

export async function updatePlan(id: string, body: PlatformPlanUpdate): Promise<PlatformPlan> {
  const { data } = await apiClient.patch<PlatformPlan>(`/royal-overseas/plans/${id}`, body);
  return data;
}

export async function getSchoolUsage(schoolId: string): Promise<PlanUsage> {
  const { data } = await apiClient.get<PlanUsage>(
    `/royal-overseas/schools/${schoolId}/usage`,
  );
  return data;
}

export interface Entitlements {
  plan: string;
  modules: string[];
  override?: Record<string, unknown> | null;
}

export async function getMyEntitlements(): Promise<Entitlements> {
  const { data } = await apiClient.get<Entitlements>('/platform/entitlements/me');
  return data;
}

export async function getMyPlanUsage(): Promise<PlanUsage> {
  const { data } = await apiClient.get<PlanUsage>('/platform/usage/me');
  return data;
}

export interface WhiteLabelUpdate {
  brand_primary_color?: string | null;
  brand_logo_url?: string | null;
  custom_domain?: string | null;
}

export interface PlatformApiKey {
  id: string;
  school_id: string;
  name: string;
  key_prefix: string;
  last_used_at?: string | null;
  revoked_at?: string | null;
  created_at?: string | null;
}

export interface PlatformApiKeyCreated extends PlatformApiKey {
  api_key: string;
}

export async function updateWhiteLabel(
  schoolId: string,
  body: WhiteLabelUpdate,
): Promise<unknown> {
  const { data } = await apiClient.patch(`/platform/schools/${schoolId}/white-label`, body);
  return data;
}

export async function listApiKeys(schoolId: string): Promise<PlatformApiKey[]> {
  const { data } = await apiClient.get<PlatformApiKey[]>('/platform/api-keys', {
    params: { school_id: schoolId },
  });
  return data;
}

export async function createApiKey(body: {
  school_id: string;
  name: string;
}): Promise<PlatformApiKeyCreated> {
  const { data } = await apiClient.post<PlatformApiKeyCreated>('/platform/api-keys', body);
  return data;
}

export async function revokeApiKey(keyId: string, schoolId: string): Promise<PlatformApiKey> {
  const { data } = await apiClient.post<PlatformApiKey>(
    `/platform/api-keys/${keyId}/revoke`,
    null,
    { params: { school_id: schoolId } },
  );
  return data;
}

export async function listAgencyAgents(agencyId: string): Promise<AgencyAgent[]> {
  const { data } = await apiClient.get<AgencyAgent[]>(
    `/royal-overseas/agencies/${agencyId}/agents`,
  );
  return data;
}

export async function assignAgencyAgent(
  agencyId: string,
  body: AgencyAgentCreate,
): Promise<AgencyAgent> {
  const { data } = await apiClient.post<AgencyAgent>(
    `/royal-overseas/agencies/${agencyId}/agents`,
    body,
  );
  return data;
}

export async function removeAgencyAgent(agencyId: string, agentId: string): Promise<void> {
  await apiClient.delete(`/royal-overseas/agencies/${agencyId}/agents/${agentId}`);
}

